import DataGrid from '@components/DataGrid';
import { Alert, Box, Button, Fab, Stack, Typography } from '@mui/material';
import { UserWithClaims, UserCreationForm } from '@synergetx/puppet-interview-types';
import {
    getUsersColumns,
    USERS_COLUMN_VISIBILITY,
    USERS_INITIAL_SORT,
} from 'constants/usersTableColumns';
import { useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CreateUserDialog from '@components/CreateUserDialog';
import UserFilterButtonGroup from 'adminManagement/UserFilterButtonGroup';
import { useUsersWithClaims } from 'hooks/useUsersWithClaims';
import { getNewUser, getUserFromExisting } from '@modules/user-utils';
import { useInterviewOptions } from '@providers/InterviewsProvider';

export default function UsersManager() {
    const { users, error, refreshUsers, setUsers } = useUsersWithClaims();
    const [userToEdit, setUserToEdit] = useState<UserCreationForm>();
    const { selectedInterview } = useInterviewOptions();
    const groupId = selectedInterview?.groupId;
    const interviewId = selectedInterview?.id;

    const [usersToShow, setUsersToShow] = useState<UserWithClaims[] | null>([]);
    const columns = useMemo(() => getUsersColumns(refreshUsers), [refreshUsers]);

    if (!interviewId || !groupId) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0, // Allow flex shrinking
            }}
        >
            <DataGrid
                columns={columns}
                rows={usersToShow ?? []}
                loading={users === null}
                getRowId={(row) => row.id}
                initialSortModel={USERS_INITIAL_SORT}
                columnVisibilityModel={USERS_COLUMN_VISIBILITY}
                onRowClick={(row) => setUserToEdit(getUserFromExisting(row))}
                toolbar={
                    <>
                        <Typography variant="h5" sx={{ mr: 2 }}>
                            Manage User Access
                        </Typography>
                        <UserFilterButtonGroup users={users} setUsersToShow={setUsersToShow} />
                    </>
                }
                emptyMessage={
                    error ? (
                        <Stack spacing={2} sx={{ alignItems: 'center' }}>
                            <Alert severity="error" sx={{ textAlign: 'left' }}>
                                {error}
                            </Alert>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={() => refreshUsers()}
                            >
                                Try again
                            </Button>
                        </Stack>
                    ) : (
                        'No users have been added to this project yet'
                    )
                }
            />
            {userToEdit ? (
                <CreateUserDialog
                    setUsers={setUsers}
                    userToEdit={userToEdit}
                    setUserToEdit={setUserToEdit}
                />
            ) : null}

            <Box sx={{ position: 'fixed', zIndex: 100, bottom: 60, right: 25 }}>
                <Fab
                    color="primary"
                    onClick={() => setUserToEdit(getNewUser(interviewId, groupId))}
                    variant="extended"
                >
                    <AddIcon sx={{ color: 'white', mr: 1 }} />
                    Add User
                </Fab>
            </Box>
        </Box>
    );
}
